import React, { ReactNode } from "react"

export type ButtonVariant =
  "primary" | "secondary" | "ghost" | "gradient" | "gradientPill"
export type ButtonSize = "sm" | "md" | "lg"

export type ButtonProps =
  | (React.ButtonHTMLAttributes<HTMLButtonElement> & {
      as?: "button"
      href?: string
      variant?: ButtonVariant
      size?: ButtonSize
      trailingIcon?: ReactNode
      trailingInCircle?: boolean
      trailingCircleClassName?: string
    })
  | (React.AnchorHTMLAttributes<HTMLAnchorElement> & {
      as: "a"
      href: string
      variant?: ButtonVariant
      size?: ButtonSize
      trailingIcon?: ReactNode
      trailingInCircle?: boolean
      trailingCircleClassName?: string
    })

const base =
  "group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full cursor-pointer font-medium focus:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
const variants: Record<ButtonVariant, string> = {
  primary:
    "bg-brand text-white hover:bg-brand-600 focus-visible:ring-brand dark:bg-jungle-500 dark:hover:bg-jungle-500",
  secondary:
    "bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 focus-visible:ring-slate-400",
  ghost: "bg-transparent text-slate-900 hover:bg-slate-100 focus-visible:ring-slate-400",
  gradient:
    "rounded-full bg-linear-to-r from-brand to-brand-600 text-white hover:from-brand-600 hover:to-brand-700 focus-visible:ring-brand shadow-lg transition-transform will-change-transform dark:from-jungle-500 dark:hover:from-jungle-500 dark:to-jungle-700 dark:hover:to-jungle-700",
  gradientPill:
    // Пилюля-CTA, визуально совпадает с variant=gradient (включая hover),
    // отличия только в расчёте внутренних отступов (spacing)
    "rounded-full bg-linear-to-r from-brand to-brand-600 text-white hover:from-brand-600 hover:to-brand-700 focus-visible:ring-brand shadow-lg transition-transform will-change-transform dark:from-jungle-500 dark:hover:from-jungle-500 dark:to-jungle-700 dark:hover:to-jungle-700",
}
const sizes: Record<ButtonSize, string> = {
  sm: "h-9 px-3 text-sm",
  md: "h-10 px-4",
  lg: "h-12 px-6 text-base",
}

const Button: React.FC<ButtonProps> = (props) => {
  const {
    variant = "gradientPill",
    size = "md",
    className = "",
    trailingIcon,
    trailingInCircle = true,
    trailingCircleClassName = "",
    ...rest
  } = props as any
  const v = variant as ButtonVariant
  const s = size as ButtonSize

  // Спейсинг для gradientPill — адаптируется к наличию trailingIcon в кружке
  const spacing = (() => {
    if (v !== "gradientPill") {
      return sizes[s]
    }
    const hasTrailingCircle = Boolean(trailingIcon) && Boolean(trailingInCircle)
    if (s === "lg") {
      return hasTrailingCircle ? "pl-6 pr-2 py-2 text-base" : "px-6 py-2 text-base"
    }
    if (s === "sm") {
      return hasTrailingCircle ? "pl-5 pr-2 py-2 text-sm" : "px-4 py-2 text-sm"
    }
    // md по умолчанию
    return hasTrailingCircle ? "pl-5 pr-1.5 py-1.5" : "px-5 py-1.5"
  })()

  const classes = `${base} ${variants[v]} ${spacing} ${className}`.trim()

  const trailingClassName = [
    trailingInCircle
      ? "inline-flex size-8 items-center justify-center rounded-full bg-white text-brand-600 dark:bg-white dark:text-jungle-600 transition-colors group-hover:bg-transparent group-hover:text-white"
      : "-mr-1",
    trailingCircleClassName,
  ]
    .filter((value) => Boolean(value) && value.trim().length > 0)
    .join(" ")

  const trailing = trailingIcon ? (
    <span aria-hidden className={trailingClassName || undefined}>
      {trailingIcon}
    </span>
  ) : null

  if ((props as any).as === "a") {
    const { href, ...anchorRest } = rest as React.AnchorHTMLAttributes<HTMLAnchorElement>
    return (
      <a href={href} {...anchorRest} className={classes}>
        {props.children}
        {trailing}
      </a>
    )
  }

  return (
    <button
      {...(rest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      className={classes}
    >
      {props.children}
      {trailing}
    </button>
  )
}

export default Button
